import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { focusRing, focusRingOn, visuallyHiddenCss } from 'ui/a11y';
import { VisuallyHidden } from 'ui/visually-hidden';

import { AccountVariant } from './use-header-model';
import { up } from './breakpoints';
import { ChevronIcon, LogoutIcon, CheckIcon, GlobeIcon } from './icons';
import { LanguageSwitch } from './language-switch';
import { useDismissable } from './use-dismissable';

const Wrap = styled.div`
  display: flex;

  /*
   * position:relative only from sm up, where the panel anchors locally to this trigger (matching
   * the desktop artboards' own "top: calc(100% + 8px); right: 0" on this same wrapper). Below sm,
   * this stays position:static so the panel's position:absolute resolves against the bar
   * (index.tsx's Bar, itself position:relative) instead of this 44px-wide wrapper — the panel
   * needs the full bar width there (V2-Account-390: "left: 8px; right: 8px" spanning the viewport).
   */
  ${up('sm')} {
    position: relative;
  }
`;

const Avatar = styled.span<{ $size: number }>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${p => p.theme.colors.primary};
  color: ${p => p.theme.colors.brown};
  font-family: ${p => p.theme.font.family};
  font-size: 15px;
  font-weight: ${p => p.theme.font.weight.bold};
`;

// 32px at xs/sm/md, 36px at lg/xl — the trigger's own avatar; the panel identity row's is always 40.
const TriggerAvatar = styled(Avatar).attrs({ $size: 32 })`
  ${up('lg')} {
    width: 36px;
    height: 36px;
  }
`;

const Greeting = styled.span`
  display: none;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  text-align: right;

  ${up('lg')} {
    display: flex;
  }
`;

const GreetingName = styled.span`
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
  font-weight: ${p => p.theme.font.weight.bold};
  white-space: nowrap;
  color: ${p => p.theme.colors.brown};
`;

const GreetingRole = styled.span`
  font-family: ${p => p.theme.font.family};
  font-size: 14px;
  white-space: nowrap;
  color: ${p => p.theme.colors.muted};
`;

const Chevron = styled(ChevronIcon)`
  display: none;

  ${up('sm')} {
    display: block;
  }

  [aria-expanded='true'] > & {
    transform: rotate(180deg);
  }
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 44px;
  height: 44px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 22px;
  color: ${p => p.theme.colors.brown};
  cursor: pointer;

  &[aria-expanded='true'] {
    background: ${p => p.theme.colors.yellowPale};
  }

  ${up('sm')} {
    width: auto;
    height: 44px;
    padding: 0 8px 0 6px;
  }

  ${up('lg')} {
    height: 52px;
    gap: 10px;
    padding: 0 10px 0 14px;
    border-radius: 26px;
  }

  ${focusRing}
`;

const Panel = styled.div`
  position: absolute;
  z-index: 10;
  /*
   * Below sm, Wrap is position:static (see above), so this resolves against the bar itself —
   * 100% is the bar's own height (56px at xs), giving exactly "bar bottom edge + 8px" per
   * V2-Account-390, and left/right: 8px spans the viewport minus 8px on each side, not just this
   * 44px trigger's own width.
   */
  top: calc(100% + 8px);
  left: 8px;
  right: 8px;
  box-sizing: border-box;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  border-radius: 8px; /* artboard-literal — not tokens.radiusLg (6px) */
  box-shadow: 0 12px 32px rgba(51, 42, 35, 0.18); /* artboard-literal, not a tokens.shadow preset */
  color: ${p => p.theme.colors.brown};
  text-align: left;
  padding: ${p => p.theme.space[1]} 0;

  ${up('sm')} {
    /* From sm up, Wrap is position:relative again, so this now anchors to the trigger, matching
       the desktop artboards' own 320px right-aligned panel. */
    left: auto;
    right: 0;
    width: 320px;
  }
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.space.onePointFive};
  padding: ${p => p.theme.space.onePointFive} ${p => p.theme.space[2]};
`;

const IdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IdentityName = styled.span`
  font-family: ${p => p.theme.font.family};
  font-size: 18px;
  font-weight: ${p => p.theme.font.weight.bold};
`;

const IdentityRole = styled.span`
  font-family: ${p => p.theme.font.family};
  font-size: 14px;
  color: ${p => p.theme.colors.muted};
`;

const Divider = styled.div`
  height: 1px;
  background: ${p => p.theme.colors.borderSubtle};
  margin: ${p => p.theme.space.threeQuarter} 0;
`;

const RoleSectionLabel = styled.div`
  padding: ${p => p.theme.space[1]} ${p => p.theme.space[2]} ${p => p.theme.space.half};
  font-family: ${p => p.theme.font.family};
  font-size: 14px;
  color: ${p => p.theme.colors.muted};
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const RoleRowLabelWrap = styled.label`
  display: flex;
  width: 100%;
  cursor: pointer;
`;

// Visually hidden (kept in the a11y tree) native radio, matching `ui/selectable-card.tsx`'s
// `HiddenRadio` pattern — its focus ring is moved onto the visible `RoleRowBox` sibling below,
// since a clipped input can't show its own outline.
const HiddenRadio = styled.input`
  ${visuallyHiddenCss}
`;

const RoleRowBox = styled.span<{ $selected: boolean }>`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.space.onePointFive};
  padding: 0 ${p => p.theme.space[2]};
  background: ${p => (p.$selected ? p.theme.colors.yellowPale : 'transparent')};
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
  font-weight: ${p => (p.$selected ? p.theme.font.weight.bold : p.theme.font.weight.regular)};
  color: ${p => p.theme.colors.brown};

  ${RoleRowLabelWrap}:hover & {
    background: ${p => p.theme.colors.yellowPale};
  }

  ${focusRingOn(css`${HiddenRadio}:focus-visible + &`)}
`;

const RoleRowLabel = styled.span`
  flex-grow: 1;
`;

const KeelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.space.onePointFive};
  padding: ${p => p.theme.space.threeQuarter} ${p => p.theme.space[2]};
`;

const KeelIcon = styled.span`
  display: flex;
  color: ${p => p.theme.colors.muted};
`;

const KeelLabel = styled.span`
  flex-grow: 1;
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
`;

const LogoutButton = styled.button`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.space.onePointFive};
  padding: 0 ${p => p.theme.space[2]};
  background: transparent;
  border: 0;
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
  font-weight: ${p => p.theme.font.weight.bold};
  color: ${p => p.theme.colors.brown};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${p => p.theme.colors.yellowPale};
  }

  ${focusRing}
`;

const LogoutIconWrap = styled.span`
  display: flex;
  color: ${p => p.theme.colors.red};
`;

export interface AccountMenuProps {
  variant: Extract<AccountVariant, 'msal-allowed' | 'msal-not-allowed' | 'guest-checkpoint'>;
  firstName: string;
  roleLabel: string;
  showRolePicker: boolean;
  switchableRoles: string[];
  activeRole: string | null;
  onSwitchRole: (role: string) => void;
  showLanguage: boolean;
  language: string;
  onChangeLanguage: (lng: string) => void;
  onLogout: () => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

/**
 * The account trigger + disclosure panel (`V2-Account-*` artboards). One component covers all
 * three non-"none" account variants — `msal-not-allowed` just skips the role radios and language
 * row; `guest-checkpoint` skips the role radios only.
 */
export function AccountMenu({
  variant,
  firstName,
  roleLabel,
  showRolePicker,
  switchableRoles,
  activeRole,
  onSwitchRole,
  showLanguage,
  language,
  onChangeLanguage,
  onLogout,
  open,
  onToggle,
  onClose,
}: AccountMenuProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useDismissable(open, wrapRef, onClose, triggerRef);

  const initial = firstName ? firstName.charAt(0).toUpperCase() : '';
  const showRoleLabel = variant === 'msal-allowed' && roleLabel;

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? 'lis-account-panel' : undefined}
        onClick={onToggle}
      >
        {/*
          Content-based accessible name (not `aria-label`) so it never fights the visible
          "Tere, X!" greeting the lg+ trigger also shows (WCAG 2.5.3 Label in Name) — `Greeting`
          is `display:none` below lg, so only this hidden span contributes there; at lg+ both
          contribute, and the concatenation still contains the visible label verbatim.
        */}
        <VisuallyHidden>{t('header.account', { firstName })}</VisuallyHidden>
        <Greeting>
          <GreetingName>{t('userWelcome', { firstName })}</GreetingName>
          {showRoleLabel && <GreetingRole>{roleLabel}</GreetingRole>}
        </Greeting>
        <TriggerAvatar aria-hidden="true">{initial}</TriggerAvatar>
        <Chevron size={16} />
      </Trigger>
      {open && (
        <Panel id="lis-account-panel" role="group" aria-label={t('header.account', { firstName })}>
          <IdentityRow>
            <Avatar $size={40} aria-hidden="true">
              {initial}
            </Avatar>
            <IdentityText>
              <IdentityName>{t('userWelcome', { firstName })}</IdentityName>
              {showRoleLabel && <IdentityRole>{roleLabel}</IdentityRole>}
            </IdentityText>
          </IdentityRow>

          {showRolePicker && (
            <>
              <Divider aria-hidden="true" />
              <RoleSectionLabel id="lis-account-role-label">{t('roles.label')}</RoleSectionLabel>
              <RadioGroup role="radiogroup" aria-labelledby="lis-account-role-label">
                {switchableRoles.map(role => {
                  const selected = role === activeRole;
                  return (
                    <RoleRowLabelWrap key={role}>
                      <HiddenRadio
                        type="radio"
                        name="lis-account-role"
                        value={role}
                        checked={selected}
                        onChange={() => onSwitchRole(role)}
                      />
                      <RoleRowBox $selected={selected}>
                        <RoleRowLabel>{t(`roles.${role}`)}</RoleRowLabel>
                        {selected && <CheckIcon size={20} />}
                      </RoleRowBox>
                    </RoleRowLabelWrap>
                  );
                })}
              </RadioGroup>
            </>
          )}

          {showLanguage && (
            <>
              <Divider aria-hidden="true" />
              <KeelRow>
                <KeelIcon aria-hidden="true">
                  <GlobeIcon size={20} />
                </KeelIcon>
                <KeelLabel>{t('header.language')}</KeelLabel>
                <LanguageSwitch language={language} onChange={onChangeLanguage} />
              </KeelRow>
            </>
          )}

          <Divider aria-hidden="true" />
          <LogoutButton type="button" onClick={onLogout}>
            <LogoutIconWrap aria-hidden="true">
              <LogoutIcon size={20} />
            </LogoutIconWrap>
            <span>{t('header.logout')}</span>
          </LogoutButton>
        </Panel>
      )}
    </Wrap>
  );
}

export default AccountMenu;
